"use client";

import { FiBell, FiCheck, FiSmartphone } from "react-icons/fi";
import {
  Badge,
  Button,
  Group,
  Modal,
  Stack,
  Text,
} from "@/components/ui/elements";
import {
  AppStoreButton,
  GooglePlayStoreButton,
} from "@/components/ui/parts/appStore";

import {
  APP_STORE_URL,
  PLAY_STORE_URL,
} from "./_function/nativeAppReleaseConstants";

type NativeAppReleaseModalProps = {
  opened: boolean;
  onConfirm: () => void;
};

const sectionCardStyle = {
  borderRadius: "12px",
  padding: "14px 16px",
} as const;

const storeBadgeWrapStyle = {
  display: "flex",
  justifyContent: "center",
} as const;

export function NativeAppReleaseModal({
  opened,
  onConfirm,
}: NativeAppReleaseModalProps) {
  return (
    <Modal
      opened={opened}
      close={onConfirm}
      centered
      size="md"
      padding="xl"
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Stack gap="md">
        <Badge
          size="sm"
          variant="light"
          color="teal"
          radius="xl"
          fw={600}
          w="fit-content"
          leftSection={<FiBell size={14} aria-hidden />}
          styles={{
            root: {
              paddingLeft: 10,
              paddingRight: 10,
              textTransform: "none",
            },
          }}
        >
          ケアリンクからのお知らせ
        </Badge>

        <div
          style={{
            background: "linear-gradient(135deg, #e0f7fa 0%, #e8f5e9 100%)",
            borderRadius: "16px",
            padding: "20px 16px",
            border: "1px solid #b2dfdb",
          }}
        >
          <Stack gap="sm" align="center">
            <Text
              fw={700}
              ta="center"
              style={{
                fontSize: "22px",
                color: "#00695c",
                lineHeight: 1.45,
              }}
            >
              アプリ
              <br />
              公開のお知らせ ✨
            </Text>
            <Text size="sm" ta="center" c="dark.6" lh={1.7}>
              いつもケアリンクをご利用いただき
              <br />
              ありがとうございます。
            </Text>
          </Stack>
        </div>

        <Text size="sm" lh={1.7} c="dark.6">
          このたびAndroid版・iPhone版のアプリを公開いたしましたのでお知らせいたします。
        </Text>

        <div
          style={{
            ...sectionCardStyle,
            backgroundColor: "#e0f2f1",
            border: "1px solid #80cbc4",
          }}
        >
          <Stack gap="sm">
            <Group gap="xs" wrap="nowrap">
              <FiSmartphone size={18} color="#00796b" aria-hidden />
              <Text size="sm" fw={700} c="teal.8">
                Android版
              </Text>
            </Group>
            <Text size="sm" lh={1.7} c="dark.6">
              Google Play からダウンロードできます。
            </Text>
            <div style={storeBadgeWrapStyle}>
              <GooglePlayStoreButton
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
              />
            </div>
          </Stack>
        </div>

        <div
          style={{
            ...sectionCardStyle,
            backgroundColor: "#e0f2f1",
            border: "1px solid #80cbc4",
          }}
        >
          <Stack gap="sm">
            <Group gap="xs" wrap="nowrap">
              <FiSmartphone size={18} color="#00796b" aria-hidden />
              <Text size="sm" fw={700} c="teal.8">
                iPhone版
              </Text>
            </Group>
            <Text size="sm" lh={1.7} c="dark.6">
              App Store からダウンロードできます。
            </Text>
            <div style={storeBadgeWrapStyle}>
              <AppStoreButton
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
              />
            </div>
          </Stack>
        </div>

        <Button
          variant="outline"
          size="lg"
          color="gray"
          fullWidth
          onClick={onConfirm}
          styles={{
            root: {
              height: 48,
              fontSize: 15,
              fontWeight: 600,
              color: "var(--mantine-color-dark-7)",
              borderColor: "var(--mantine-color-gray-4)",
            },
          }}
        >
          <Group gap="xs" justify="center">
            <FiCheck size={18} aria-hidden />
            内容を確認しました
          </Group>
        </Button>
      </Stack>
    </Modal>
  );
}
